import { User, LoginCredentials } from '../types';
import { sessionStorageUtils } from '../common/utils/sessionStorage';
import { apiClient } from '../common/utils/apiClient';
import { API_CONFIG } from '../config/api';

// Real API authentication service
export const authService = {
  login: async (credentials: LoginCredentials): Promise<{ token: string; user: User }> => {
    try {
      const response = await apiClient.post<{ token: string; user: User }>(
        API_CONFIG.ENDPOINTS.LOGIN,
        credentials
      );
      
      // Store token in session storage
      if (response.token) {
        sessionStorageUtils.setToken(response.token);
      }
      
      return response;
    } catch (error) {
      throw new Error('Login failed. Please check your credentials.');
    }
  },

  logout: async (): Promise<void> => {
    try {
      // Clear token from session storage
      sessionStorageUtils.removeToken();
    } catch (error) {
      // Even if there's an error, clear the local token
      sessionStorageUtils.removeToken();
    }
  },

  getCurrentUser: async (): Promise<User> => {
    const token = sessionStorageUtils.getToken();
    if (!token) {
      throw new Error('No authentication token');
    }
    
    try {
      // Use validate token endpoint to get current user info
      const response = await authService.validateToken(token);
      if (response.valid && response.user) {
        return response.user;
      }
      throw new Error('Invalid token');
    } catch (error) {
      throw new Error('Failed to get current user');
    }
  },

  refreshToken: async (): Promise<{ token: string }> => {
    const token = sessionStorageUtils.getToken();
    if (!token) {
      throw new Error('No token to refresh');
    }
    
    try {
      // For now, we'll just return the current token
      // In a real implementation, you might have a refresh endpoint
      return { token };
    } catch (error) {
      throw new Error('Failed to refresh token');
    }
  },

  // Validate JWT token using real API endpoint
  validateToken: async (token: string): Promise<{ valid: boolean; user?: User }> => {
    try {
      const response = await apiClient.post<{ valid: boolean; user?: User }>(
        API_CONFIG.ENDPOINTS.VALIDATE_TOKEN,
        {},
        token
      );
      
      return response;
    } catch (error) {
      return { valid: false };
    }
  },
};
